import { __rest, __assign } from '../../../_virtual/_tslib.js';
import { Icons } from '@beeline/design-tokens/js/iconfont/icons';
import { colorBackgroundBrandPressed, colorBackgroundBrand, colorButtonOverlayBackground } from '@beeline/design-tokens/js/tokens/globals/colors';
import React__default, { forwardRef, useRef, useState, useMemo, useEffect } from 'react';
import { classNames } from '../../../helpers/classnames.js';
import { useDebounceCallback } from '../../../helpers/use-debounce-callback.js';
import { Icon } from '../../Icon/Icon.js';
import { Button } from '../Button.js';
import { DEFAULT_SETTINGS } from './consts.js';
import { calcPathDefinition, calcSettings, isErrorObject } from './utils.js';

var ProgressButton = forwardRef(function (_a, ref) {
    var _b = _a.determinateMode, determinateMode = _b === void 0 ? false : _b, _c = _a.progress, progress = _c === void 0 ? 0 : _c, _d = _a.size, size = _d === void 0 ? 'medium' : _d, _e = _a.statusDelayTime, statusDelayTime = _e === void 0 ? 4000 : _e, className = _a.className, _f = _a.variant, variant = _f === void 0 ? 'contained' : _f, children = _a.children, onClick = _a.onClick, disabled = _a.disabled, state = _a.state, props = __rest(_a, ["determinateMode", "progress", "size", "statusDelayTime", "className", "variant", "children", "onClick", "disabled", "state"]);
    var svgRef = useRef(null);
    var pathRef = useRef(null);
    var buttonRef = useRef(null);
    var _g = useState(determinateMode ? progress : 0), _progress = _g[0], _setProgress = _g[1];
    var _h = useState(Icons.Check), statusIcon = _h[0], setStatusIcon = _h[1];
    var _j = useState(false), isError = _j[0], setIsError = _j[1];
    var _k = useState(DEFAULT_SETTINGS), settings = _k[0], setSettings = _k[1];
    var _l = useDebounceCallback(function () {
        if (statusDelayTime === -1) {
            return;
        }
        _setProgress(0);
        setIsError(false);
    }, statusDelayTime), toDefaultStateWithDelay = _l[0], cancelToDefaultStateWithDelay = _l[1];
    // текущий статус загрузки, реагирует на изменение прогресса и ошибку
    var loadingState = useMemo(function () {
        if (state) {
            return state;
        }
        if (isError) {
            return 'error';
        }
        if (_progress <= 0) {
            return 'default';
        }
        if (_progress > 0 && _progress < 100) {
            return 'loading';
        }
        if (_progress >= 100) {
            return 'success';
        }
    }, [isError, _progress, state]);
    // присваиваем current и внутренней ссылке на кнопку и той, что приходит в пропсах
    // нужно для корректной работы и когда пользователю нужен внешний ref и когда нет
    var setButtonRefHandler = function (buttonElement) {
        if (buttonElement) {
            if (ref && 'current' in ref) {
                ref.current = buttonElement;
            }
            buttonRef.current = buttonElement;
        }
    };
    var isFinished = useMemo(function () { return loadingState === 'success' || loadingState === 'error'; }, [loadingState]);
    var strokeColor = useMemo(function () {
        if (variant === 'contained') {
            return colorBackgroundBrandPressed;
        }
        if (variant === 'outlined') {
            return colorBackgroundBrand;
        }
        if (variant === 'overlay') {
            return colorButtonOverlayBackground;
        }
        return 'transparent';
    }, [variant]);
    var pathDefinition = useMemo(function () { return calcPathDefinition(settings); }, [settings]);
    /**
     * Перерисовка анимации на основе новых размеров
     */
    useEffect(function () {
        var calculatedSettings = calcSettings(buttonRef);
        setSettings(calculatedSettings);
    }, [children, size, loadingState, buttonRef]);
    /**
     * обновление прогресса при изменении пропса снаружи
     * работает только в режиме определенной загрузки
     * при цикличной загрузке прогрессом полностью управляет компонент
     */
    useEffect(function () {
        if (determinateMode) {
            if (progress <= 0) {
                _setProgress(0);
            }
            if (progress > 0 && progress < 100) {
                _setProgress(progress);
            }
            if (progress >= 100) {
                _setProgress(100);
            }
        }
    }, [progress, determinateMode]);
    /**
     * Управление отображением загрузки в зависимости от текущего {@link loadingState}
     */
    useEffect(function () {
        switch (loadingState) {
            case 'default':
                break;
            case 'loading':
                break;
            case 'success':
                setStatusIcon(Icons.Check);
                toDefaultStateWithDelay();
                break;
            case 'error':
                setStatusIcon(Icons.Refresh);
                toDefaultStateWithDelay();
                break;
            default:
                throw new Error("\u041D\u0435\u0438\u0437\u0432\u0435\u0441\u0442\u043D\u043E\u0435 \u0441\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u0435 \u0437\u0430\u0433\u0440\u0443\u0437\u043A\u0438 \u0434\u043B\u044F ProgressButton: '".concat(loadingState, "'!"));
        }
    }, [loadingState, toDefaultStateWithDelay]);
    var handleClick = function () {
        setIsError(false);
        cancelToDefaultStateWithDelay();
        // работая с зацикленным лоадером - управляем значением прогресса для отрисовки загрузки, обрабатываем ошибку
        if (!determinateMode && onClick) {
            _setProgress(3);
        }
        // в любом режиме обрабатываем резолв и ошибку
        onClick === null || onClick === void 0 ? void 0 : onClick().then(function (res) {
            // если пользователь обработает ошибку
            // и вернет значение из своего блока catch, то он придет сюда
            // если это все-таки ошибка - кидаем ошибку чтобы корректно обработать статус кнопки
            if (isErrorObject(res)) {
                return Promise.reject(res);
            }
            _setProgress(100);
            return res;
        }).catch(function (err) {
            setIsError(true);
            // прокидываем ошибку дальше
            throw err;
        });
    };
    return (React__default.createElement(Button, __assign({ "data-testid": "ProgressButton", ref: setButtonRefHandler }, props, { onClick: handleClick, size: size, variant: variant, className: classNames('dsb-button-progress', "dsb-button-progress--".concat(loadingState), className), disabled: disabled || loadingState === 'loading' }),
        React__default.createElement("div", { className: classNames('dsb-button-progress__status', {
                'dsb-button-progress__status--show': isFinished,
            }) },
            React__default.createElement(Icon, { iconName: statusIcon, size: size })),
        React__default.createElement("span", { className: classNames('dsb-button-progress__content', {
                'dsb-button-progress__content--hide': isFinished,
            }) }, children),
        React__default.createElement("svg", { ref: svgRef, width: "".concat(settings.width + settings.strokeWidth), height: "".concat(settings.height + settings.strokeWidth), className: classNames('dsb-button-progress__svg') },
            React__default.createElement("path", { ref: pathRef, className: classNames('dsb-button-progress__svg-path', "dsb-button-progress__svg-path--".concat(loadingState), {
                    'dsb-button-progress__svg-path--indeterminate-loading': !determinateMode && loadingState === 'loading',
                }), d: pathDefinition, strokeDasharray: "100", strokeDashoffset: "".concat(100 - _progress), pathLength: "100", stroke: strokeColor }))));
});

export { ProgressButton };
